import { SqlTableDefinition } from "@cervval.js/common-sql-core";
import { getForeignKeyTableName } from "@cervval.js/common-sql-core/src/sql.tableDefinition.tools";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { TEMPORARY_ID_SERVICE, TemporaryIdService } from "@cervval.js/common/src/services/temporaryIds.service";
import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { CommonEvent, CommonEventHandler } from "@cervval.js/common/src/tools/event.tools";
import { ActionPersistResult } from "../../action.model";
import { DTOCacheRepository } from "../../dtos/dtoCacheRepository";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "../cache.service";

/**
 * Will be used for server or client that does not need cache
 * * When using DummyCacheService all id properties must be named "id"
 */
@Injectable(ACTION_CACHE_SERVICE)
export class DummyCacheService<ElementTypes, CacheInfo = unknown> implements ActionCacheService<ElementTypes, CacheInfo> {

    @Injected(TEMPORARY_ID_SERVICE)
    private readonly temporaryIdService!: TemporaryIdService;

    private _dummyCacheRepository = new DTOCacheRepository({ isDummy: true });
    /** map referencing an element table definition */
    private _elementTypeTableDefinitionMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>>;

    constructor(_elementTypeTableDefinitionMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>>) {
        this._elementTypeTableDefinitionMap = _elementTypeTableDefinitionMap;
    }

    /** 
     * @inheritdoc
     */
    public dispose(p: { disposeRepositories: boolean }): void {
        // nothing to do
    }

    /**
     * Get the table definition map used in cache
     * @returns {Map<keyof ElementTypes, SqlTableDefinition>}
     */
    public getTableDefinitionMap(): Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>> {
        return this._elementTypeTableDefinitionMap;
    }

    /**
     * 
     * @param elementTypeName 
     * @returns {SqlTableDefinition<string, string>} sql table definition of given element
     */
    public getTableDefinition(elementTypeName: keyof ElementTypes): SqlTableDefinition<keyof ElementTypes, string> | undefined {
        return undefined;
    }

    /** @inheritdoc */
    public getForeignKeyTableName<ET extends keyof ElementTypes>(elementTypeName: ET, fieldName: keyof ElementTypes[ET]): keyof ElementTypes | null {
        return getForeignKeyTableName(this._elementTypeTableDefinitionMap, elementTypeName, fieldName);
    }

    /**
     * Returns a dummy DTOCacheRepository (with no storage in cache)
     */
    public getDTOCacheRepository<TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName]>(elementTypeName: TypeName): DTOCacheRepository<ElementType, CacheInfo> {
        return this._dummyCacheRepository as DTOCacheRepository<ElementType, CacheInfo>;
    }

    /**
     * Returns a dummy DTOCacheRepository (with no storage in cache)
     */
    public findDTOCacheRepository<TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName]>(elementTypeName: TypeName): DTOCacheRepository<ElementType, CacheInfo> | undefined {
        return this._dummyCacheRepository as DTOCacheRepository<ElementType, CacheInfo>;
    }

    /**
     * Does nothing since there is no cache to refresh
     */
    public refreshDTOCacheRepository<TypeName extends keyof ElementTypes>(elementTypeName: TypeName): Promise<void> {
        return Promise.resolve(undefined);
    }

    /**
     * Set a temporary id on an element
     * * all id properties must be named "id"
     */
    public setTemporaryId<TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName]>(elementTypeName: TypeName, elementToPrepare: ElementType): void {
        const nextId = this.temporaryIdService.getNextId(String(elementTypeName));
        if (typeof elementToPrepare !== "object" || elementToPrepare == null) { return; }
        setProp(elementToPrepare, "id", nextId);
    }

    /**
     * Does nothing since there is no cache to refresh
     */
    public updatePersistedElements(actionPersistResult: ActionPersistResult): void {
    }

    /** Triggered when the whole cache needs to be tagged dirty (usually on persistenceError) */
    @CommonEvent()
    public cacheDirty!: CommonEventHandler<{ reason: "persistenceError" | string }>;

    /** 
     * Triggered when the "refresh button" state needs to be changed (usually after websocket message)
     */
    @CommonEvent()
    public dirtyStateChanged!: CommonEventHandler<void>;
}
